import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Mail, MapPin, Phone, Clock, Send } from 'lucide-react';
import BreathingRoomAttribution from '../components/BreathingRoomAttribution';

const BOOKING_URL = "https://breathingroombda.com/appointments-2/";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsSubmitting(false);
    setSubmitted(true);
  };

  return (
    <div className="bg-cream pt-24" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <p className="text-terracotta font-body text-sm tracking-widest uppercase mb-4">
              Contact
            </p>
            <h1 className="font-heading text-5xl md:text-6xl text-forest leading-[1.1] mb-6">
              Let's Connect
            </h1>
            <p className="text-stone-600 text-lg font-body leading-relaxed">
              Whether you have questions about my services, want to book a session, 
              or just want to say hello—I'd love to hear from you.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {submitted ? (
                <div className="bg-stone-100 p-8 md:p-10 rounded-2xl text-center" data-testid="contact-success">
                  <div className="w-16 h-16 bg-terracotta/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Send size={28} className="text-terracotta" />
                  </div>
                  <h3 className="font-heading text-2xl text-forest mb-4">
                    Message Sent!
                  </h3>
                  <p className="text-stone-600 font-body">
                    Thank you for reaching out. I'll get back to you within 24-48 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-forest font-body font-medium mb-2">
                        Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        data-testid="contact-name-input"
                        className="w-full bg-white border border-stone-300 rounded-xl px-5 py-4 text-forest placeholder:text-stone-400 focus:outline-none focus:border-terracotta transition-colors"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="block text-forest font-body font-medium mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        data-testid="contact-email-input"
                        className="w-full bg-white border border-stone-300 rounded-xl px-5 py-4 text-forest placeholder:text-stone-400 focus:outline-none focus:border-terracotta transition-colors"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-forest font-body font-medium mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        data-testid="contact-phone-input"
                        className="w-full bg-white border border-stone-300 rounded-xl px-5 py-4 text-forest placeholder:text-stone-400 focus:outline-none focus:border-terracotta transition-colors"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div>
                      <label className="block text-forest font-body font-medium mb-2">
                        Subject *
                      </label>
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        data-testid="contact-subject-select"
                        className="w-full bg-white border border-stone-300 rounded-xl px-5 py-4 text-forest focus:outline-none focus:border-terracotta transition-colors"
                      >
                        <option value="">Select a topic</option>
                        <option value="movement">Restorative Movement</option>
                        <option value="health">Health Coaching</option>
                        <option value="holistic">Holistic Coaching (IMESP)</option>
                        <option value="events">Events & Workshops</option>
                        <option value="general">General Inquiry</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-forest font-body font-medium mb-2">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      data-testid="contact-message-input"
                      className="w-full bg-white border border-stone-300 rounded-xl px-5 py-4 text-forest placeholder:text-stone-400 focus:outline-none focus:border-terracotta transition-colors resize-none"
                      placeholder="Tell me a bit about yourself and what you're looking for..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="contact-submit-btn"
                    className="w-full bg-forest text-cream px-8 py-4 rounded-full font-body font-medium hover:bg-forest-dark transition-all duration-300 inline-flex items-center justify-center gap-2 disabled:opacity-70"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    <ArrowRight size={18} />
                  </button>
                </form>
              )}
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="space-y-8"
            >
              {/* Book a Session Card */}
              <div className="bg-forest text-cream p-8 rounded-2xl">
                <h3 className="font-heading text-2xl mb-4">
                  Ready to Book?
                </h3>
                <p className="text-cream/80 font-body mb-6">
                  Skip the form and book directly through my scheduling system.
                </p>
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-book-btn"
                  className="bg-terracotta text-white px-6 py-3 rounded-full font-body font-medium hover:bg-terracotta-dark transition-all duration-300 inline-flex items-center gap-2"
                >
                  Book a Session
                  <ArrowRight size={16} />
                </a>
                <div className="mt-5">
                  <BreathingRoomAttribution align="left" theme="dark" />
                </div>
              </div>

              {/* Contact Details */}
              <div className="bg-stone-100 p-8 rounded-2xl space-y-6">
                <h3 className="font-heading text-xl text-forest">
                  Contact Information
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-terracotta/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail size={18} className="text-terracotta" />
                    </div>
                    <div>
                      <p className="font-body font-medium text-forest">Email</p>
                      <a href="mailto:heyjbennett@yahoo.ca" className="text-stone-600 font-body hover:text-terracotta transition-colors">heyjbennett@yahoo.ca</a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-terracotta/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin size={18} className="text-terracotta" />
                    </div>
                    <div>
                      <p className="font-body font-medium text-forest">Location</p>
                      <p className="text-stone-600 font-body">Breathing Room<br />9 Par La Ville<br />Hamilton, Bermuda HM11</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-terracotta/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock size={18} className="text-terracotta" />
                    </div>
                    <div>
                      <p className="font-body font-medium text-forest">Response Time</p>
                      <p className="text-stone-600 font-body">Within 24-48 hours</p>
                    </div>
                  </div>
                </div>

                {/* Social Links */}
                <div className="pt-4 border-t border-stone-200">
                  <p className="font-body font-medium text-forest mb-3">Follow Along</p>
                  <div className="flex gap-3">
                    <a
                      href="https://www.instagram.com/groundedwellnesswithjill"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-stone-600 hover:text-terracotta transition-colors font-body text-sm"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                      @groundedwellnesswithjill
                    </a>
                  </div>
                  <div className="flex gap-3 mt-2">
                    <a
                      href="https://www.facebook.com/groundedwellnesswithjill"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-stone-600 hover:text-terracotta transition-colors font-body text-sm"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                      Grounded Wellness with Jill
                    </a>
                  </div>
                </div>
              </div>

              {/* Note */}
              <div className="bg-terracotta/10 p-6 rounded-xl">
                <p className="text-forest font-body text-sm leading-relaxed">
                  <strong>A note on my approach:</strong> I take time to respond thoughtfully 
                  to each inquiry. If your situation is urgent, please book a session directly 
                  or indicate urgency in your message.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
